import Projection from "../../Projection";
import Vector from "../math/Vector";
import Grid from "./Grid";
import GridZone from "./GridZone";
import WorldGrid from "./WorldGrid";

export default class GridInversionOfControl {
    constructor() {        
    }

    /**
     * @param {WorldGrid} worldGrid
     * @param {Projection} projection 
     * @param {Function} gridCallback Accepts a grid as a parameter
     */
    static allVisibleGrids(worldGrid, projection, gridCallback) {
        const grids = worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (grid && grid.loaded && projection.isVisibleGridBox(grid)) {
                gridCallback(grid);
            }
        }
    }

    /**
     * @param {WorldGrid} worldGrid
     * @param {Projection} projection 
     * @param {Function} regionCallback Accepts a region as a parameter
     */
    static allVisibleRegions(worldGrid, projection, regionCallback) {
        const grids = worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (grid && grid.loaded && projection.isVisibleGridBox(grid)) {
                this.allVisibleRegionsInGrid(grid, projection, regionCallback);
            }
        }
    }
    
    /**
     * @param {WorldGrid} worldGrid
     * @param {Projection} projection 
     * @param {Function} tileCallback Accepts a GridTile as a parameter
     */
    static allVisibleTiles(worldGrid, projection, tileCallback) {
        const grids = worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (!grid) {
                continue;
            }

            if (projection.isVisibleGridBox(grid)) {
                this.allVisibleTilesInGrid(grid, projection, tileCallback);
            }
        }
    }

    /**
     * @param {WorldGrid} worldGrid
     * @param {Projection} projection 
     * @param {Vector} nearbyOrigin 
     * @param {number} nearbyRadius The radius in pixels, not tiles.
     * @param {Function} regionCallback Accepts a region as a parameter
     */
    static allNearbyRegions(worldGrid, projection, nearbyOrigin, nearbyRadius, regionCallback) {
        const grids = worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];

            if (!grid || !grid.isLoaded()) {
                continue;
            }

            if (grid.contains(nearbyOrigin) || projection.isNearby(grid, nearbyOrigin, nearbyRadius)) {
                this.allNearbyRegionsInGrid(grid, projection, regionCallback, nearbyOrigin, nearbyRadius);
            }
        }
    }

    /**
     * @param {Grid} grid 
     * @param {Projection} projection 
     * @param {Function} tileCallback Accepts a GridTile as a parameter
     */
    static allVisibleTilesInGrid(grid, projection, tileCallback) {
        if (!grid) {
            return;
        }
        
        // Check to be sure that the grid is initialized.
        const zones = grid.zones;
        for (let z = 0; z < zones.length; z++) {
            const zone = zones[z];
            if (projection.isVisibleGridBox(zone)) {
                const regions = zone.getRegions();
                for (let r = 0; r < regions.length; r++) {
                    const region = regions[r];
                    if (projection.isVisibleGridBox(region)) {
                        const tiles = region.getTiles();
                        for (let t = 0; t < tiles.length; t++) {
                            const tile = tiles[t];
                            if (projection.isVisibleGridBox(tile)) {
                                tileCallback(tile);
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * @param {Grid} grid 
     * @param {Function} tileCallback Accepts a GridTile as a parameter
     */
    static allTilesInGrid(grid, tileCallback) {
        if (!grid) {
            return;
        }
        
        // Check to be sure that the grid is initialized.
        const zones = grid.zones;
        for (let z = 0; z < zones.length; z++) {
            const regions = zones[z].getRegions();
            for (let r = 0; r < regions.length; r++) {
                const tiles = regions[r].getTiles();
                for (let t = 0; t < tiles.length; t++) {
                    tileCallback(tiles[t]);
                }
            }
        }
    }

    /**
     * @param {GridZone} zone
     * @param {Function} tileCallback Accepts a GridTile as a parameter
     */
    static allTilesInGridZone(zone, tileCallback) {
        if (!zone) {
            return;
        }
        
        const regions = zone.getRegions();
        for (let r = 0; r < regions.length; r++) {
            const tiles = regions[r].getTiles();
            for (let t = 0; t < tiles.length; t++) {
                tileCallback(tiles[t]);
            }
        }
    }

    /**
     * @private
     * @param {Grid} grid 
     * @param {Projection} projection 
     * @param {Function} regionCallback Accepts a region as a parameter
     */
    static allVisibleRegionsInGrid(grid, projection, regionCallback) {
        if (!grid) {
            return;
        }
        
        const zones = grid.zones;
        for (let z = 0; z < zones.length; z++) {
            const zone = zones[z];
            if (projection.isVisibleGridBox(zone)) {
                const regions = zone.getRegions();
                for (let r = 0; r < regions.length; r++) {
                    const region = regions[r];
                    if (projection.isVisibleGridBox(region)) {
                        regionCallback(region);
                    }
                }
            }
        }
    }

    /**
     * @private
     * @param {Grid} grid 
     * @param {Projection} projection 
     * @param {Function} regionCallback Accepts a region as a parameter
     * @param {Vector} nearbyOrigin 
     * @param {number} nearbyRadius The radius in pixels, not tiles.
     */
    static allNearbyRegionsInGrid(grid, projection, regionCallback, nearbyOrigin, nearbyRadius) {
        if (!grid) {
            return;
        }

        const zones = grid.zones;
        for (let z = 0; z < zones.length; z++) {
            const zone = zones[z];
            if (projection.isNearby(zone, nearbyOrigin, nearbyRadius)) {
                const regions = zone.getRegions();
                for (let r = 0; r < regions.length; r++) {
                    const region = regions[r];
                    if (projection.isNearby(region, nearbyOrigin, nearbyRadius)) {
                        regionCallback(region);
                    }
                }
            }
        }
    }

    /**
     * @param {Grid} grid 
     * @param {Function} regionCallback Accepts a region as a parameter
     */
    static allRegionsInGrid(grid, regionCallback) {
        if (!grid) {
            return;
        }

        const zones = grid.zones;
        for (let z = 0; z < zones.length; z++) {
            const zone = zones[z];
            const regions = zone.getRegions();
            for (let r = 0; r < regions.length; r++) {
                regionCallback(regions[r]);
            }
        }
    }

}